import { useState, useEffect, useMemo } from 'react';
import { Box, Paper, Typography, Chip, Button, Menu, MenuItem, CircularProgress } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { getC2cDevices, getC2cSummary } from '../api/c2c';

function ColFilter({ label, options, value, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const isFiltered = value.length > 0;
  const toggle = (val) => onChange(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <Typography sx={{ color: isFiltered ? '#00bcd4' : '#7a8fa6', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}{isFiltered ? ` (${value.length})` : ''}
      </Typography>
      {options.length > 0 && (
        <>
          <Box onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: isFiltered ? '#00bcd4' : '#1e3a5f', '&:hover': { color: '#00bcd4' } }}>
            <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
          </Box>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
            PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f', minWidth: 160 } }}>
            {isFiltered && (
              <MenuItem onClick={() => { onChange([]); setAnchor(null); }} sx={{ fontSize: 11, color: '#f44336', borderBottom: '1px solid #1e3a5f', '&:hover': { backgroundColor: '#0d2137' } }}>
                Clear filter
              </MenuItem>
            )}
            {options.map(opt => {
              const selected = value.includes(opt);
              return (
                <MenuItem key={opt} onClick={() => toggle(opt)} sx={{ fontSize: 12, color: selected ? '#00bcd4' : '#c0cfe0', backgroundColor: selected ? '#0d2137' : 'transparent', '&:hover': { backgroundColor: '#0d2137' }, display: 'flex', justifyContent: 'space-between' }}>
                  {opt}
                  {selected && <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#00bcd4' }} />}
                </MenuItem>
              );
            })}
          </Menu>
        </>
      )}
    </Box>
  );
}

function StatBox({ label, value, color }) {
  return (
    <Paper sx={{ flex: 1, backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, padding: '16px 20px', textAlign: 'center' }}>
      <Typography sx={{ color: color || '#ffffff', fontSize: 36, fontWeight: 700, lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 0.5 }}>{label}</Typography>
    </Paper>
  );
}

function c2cStatusLabel(d) {
  if (d.c2c_compliant === true)  return 'Compliant';
  if (d.c2c_compliant === false) return 'Non-Compliant';
  return 'Unknown';
}

function avStatusLabel(status) {
  if (status === 'active')   return 'Current';
  if (status === 'inactive') return 'Outdated';
  if (status === 'error')    return 'Error';
  return 'Unknown';
}

function formatCheckin(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const daysAgo = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (daysAgo === 0) return 'Today';
  if (daysAgo === 1) return '1 day ago';
  return `${daysAgo} days ago`;
}

export default function ComplyToConnect({ systemId }) {
  const [devices, setDevices]       = useState([]);
  const [summary, setSummary]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [filterStatus, setFilterStatus]   = useState([]);
  const [filterAvStatus, setFilterAvStatus] = useState([]);
  const [filterType, setFilterType] = useState([]);

  useEffect(() => {
    if (!systemId) return;
    setLoading(true);
    Promise.all([getC2cDevices(systemId), getC2cSummary(systemId)])
      .then(([devs, sum]) => { setDevices(devs); setSummary(sum); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [systemId]);

  const filteredDevices = useMemo(() => devices.filter(d => {
    if (filterStatus.length > 0 && !filterStatus.includes(c2cStatusLabel(d))) return false;
    if (filterAvStatus.length > 0 && !filterAvStatus.includes(avStatusLabel(d.av_status))) return false;
    if (filterType.length > 0 && !filterType.includes(d.device_type)) return false;
    return true;
  }), [devices, filterStatus, filterAvStatus, filterType]);

  const deviceTypes = useMemo(() => Array.from(new Set(devices.map(d => d.device_type).filter(Boolean))).sort(), [devices]);

  if (loading) {
    return (
      <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: '#00bcd4' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', padding: 3 }}>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, marginBottom: 3 }}>
        <StatBox label="Total Assets"   value={summary?.total         ?? devices.length} color="#ffffff" />
        <StatBox label="Compliant"      value={summary?.compliant     ?? 0}              color="#4caf50" />
        <StatBox label="Non-Compliant"  value={summary?.non_compliant ?? 0}              color="#f44336" />
        <StatBox label="Pending / Unknown" value={summary?.pending    ?? 0}              color="#ff9800" />
      </Box>

      {devices.length === 0 ? (
        <Paper sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, padding: 4, textAlign: 'center' }}>
          <Typography sx={{ color: '#7a8fa6' }}>No C2C data yet. Deploy the ASPYN agent and configure Cisco ISE to populate this view.</Typography>
        </Paper>
      ) : (
        <Paper sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, overflow: 'hidden' }}>

          {/* Header */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '160px 130px 1fr 110px 110px 120px 100px 110px', padding: '10px 16px', borderBottom: '1px solid #1e3a5f', backgroundColor: '#0d1825' }}>
            <ColFilter label="Device"      options={[]}                                       value={[]}            onChange={() => {}} />
            <ColFilter label="IP"          options={[]}                                       value={[]}            onChange={() => {}} />
            <ColFilter label="OS"          options={[]}                                       value={[]}            onChange={() => {}} />
            <ColFilter label="Type"        options={deviceTypes}                              value={filterType}    onChange={setFilterType} />
            <ColFilter label="C2C Status"  options={['Compliant','Non-Compliant','Unknown']}  value={filterStatus}  onChange={setFilterStatus} />
            <ColFilter label="Last Checked" options={[]}                                      value={[]}            onChange={() => {}} />
            <ColFilter label="AV Status"   options={['Current','Outdated','Error','Unknown']} value={filterAvStatus} onChange={setFilterAvStatus} />
            <ColFilter label="AV Def"      options={[]}                                       value={[]}            onChange={() => {}} />
          </Box>

          {/* Rows */}
          {filteredDevices.map((d, i) => {
            const c2cLabel  = c2cStatusLabel(d);
            const c2cColor  = d.c2c_compliant === true ? '#4caf50' : d.c2c_compliant === false ? '#f44336' : '#ff9800';
            const c2cBg     = d.c2c_compliant === true ? '#0a2a1a' : d.c2c_compliant === false ? '#2a0a0a' : '#1a1a0a';
            const avLabel   = avStatusLabel(d.av_status);
            const avColor   = d.av_status === 'active' ? '#4caf50' : d.av_status === 'inactive' ? '#ff9800' : d.av_status === 'error' ? '#f44336' : '#7a8fa6';
            const avBg      = d.av_status === 'active' ? '#0a2a1a' : d.av_status === 'inactive' ? '#1a1a0a' : d.av_status === 'error' ? '#2a0a0a' : '#111827';
            return (
              <Box key={d.asset_id} sx={{
                display: 'grid',
                gridTemplateColumns: '160px 130px 1fr 110px 110px 120px 100px 110px',
                alignItems: 'center',
                padding: '11px 16px',
                borderBottom: i < filteredDevices.length - 1 ? '1px solid #0d1825' : 'none',
                '&:hover': { backgroundColor: '#0d1f33' },
                backgroundColor: d.c2c_compliant === false ? '#1a0a0a' : 'transparent',
              }}>
                <Typography sx={{ color: '#ffffff', fontWeight: 600, fontFamily: 'monospace', fontSize: 13 }}>{d.hostname}</Typography>
                <Typography sx={{ color: '#00bcd4', fontFamily: 'monospace', fontSize: 12 }}>{d.ip_address}</Typography>
                <Typography sx={{ color: '#7a8fa6', fontSize: 12 }}>{d.os || '—'}</Typography>
                <Chip label={d.device_type || 'unknown'} size="small" sx={{ backgroundColor: '#1e3a5f', color: '#00bcd4', fontWeight: 700, fontSize: 11, height: 20 }} />
                <Chip label={c2cLabel} size="small" sx={{ backgroundColor: c2cBg, color: c2cColor, fontWeight: 700, fontSize: 11, height: 20 }} />
                <Typography sx={{ color: '#7a8fa6', fontSize: 12 }}>{formatCheckin(d.c2c_last_checked)}</Typography>
                <Chip label={avLabel} size="small" sx={{ backgroundColor: avBg, color: avColor, fontWeight: 700, fontSize: 11, height: 20 }} />
                <Typography sx={{ color: '#7a8fa6', fontFamily: 'monospace', fontSize: 11 }}>{d.av_definition || '—'}</Typography>
              </Box>
            );
          })}
        </Paper>
      )}
    </Box>
  );
}
